import React, { useState } from 'react';
import { TripParams } from '../types';
import { Strategy, AlternativePlanEntry, quickStrategies, moreStrategies, strategyKey, strategyMeta, strategyShortLabel, strategyOptionLabel } from '../strategies';
import Dropdown from '../../../components/ui/Dropdown';
import { useViewportWidth } from '../../../hooks/useViewportWidth';

// How many quick-strategy pills fit beside the "More ways…" dropdown for a given
// content width. The page column is fixed (maxWidth 560, 16px padding), so the
// window width determines the content width. Below ~290px only the dropdown
// shows; the cap stays at 3 (quickStrategies never returns more).
const DROPDOWN_W = 140; // px reserved for the dropdown
const PILL_W = 120;     // px rough per-pill incl. gap
function pillsThatFit(viewportWidth: number): number {
  const content = Math.min(viewportWidth - 32, 528);
  return Math.max(0, Math.min(3, Math.floor((content - DROPDOWN_W) / PILL_W)));
}

// Trim the quick strategies to `max`, always keeping "Jody Smart Mix" — so the
// last pill standing is the Smart Mix (spec: "if only one pill fits, use it").
function visibleStrategies(quick: Strategy[], max: number): Strategy[] {
  if (max <= 0) return [];
  if (max >= quick.length) return quick;
  const smartMix = quick.find((s) => s.kind === 'multimodal');
  const others = quick.filter((s) => s.kind !== 'multimodal');
  if (!smartMix) return others.slice(0, max);
  if (max === 1) return [smartMix];
  return [...others.slice(0, max - 1), smartMix];
}

// One generator, two views of the SAME feature:
//  - 'cards'   → the prominent empty-state ("Generate your first route"): the
//                quick strategies as big cards + the "More ways…" dropdown.
//  - 'compact' → the "Explore more alternatives" bar shown once a trip has
//                routes (below the list, above the map): pills + dropdown.
// Both offer the same strategies and the same "More ways…" long tail. Each
// choice generates an alternative with the transport overridden — same AI path.

export interface AlternativeGeneratorProps {
  params: TripParams | null;
  onPick: (strategy: Strategy) => void;
  busy?: boolean;
  view: 'cards' | 'compact';
  heading: string;
  subheading?: string;
  /** Compact view only: whether the strategy row starts expanded (default true). */
  defaultExpanded?: boolean;
  /** Planner-chosen explore tags (with reasons); when present they replace the
   *  quickStrategies heuristic as the compact pills. */
  planExplore?: AlternativePlanEntry[] | null;
}

const AlternativeGenerator: React.FC<AlternativeGeneratorProps> = ({
  params, onPick, busy = false, view, heading, subheading, defaultExpanded = true, planExplore,
}) => {
  const viewportWidth = useViewportWidth();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const quick = quickStrategies(params);
  const more = moreStrategies(params);
  // Planner tags win over the heuristic pills; each carries a user-facing
  // reason (tooltip) and possibly the one out-of-selection nudge (🌱).
  const planEntries = view === 'compact' && planExplore && planExplore.length > 0 ? planExplore : null;

  // Build the "More ways…" dropdown from a strategy list. In the compact view we
  // prepend the quick strategies that didn't fit as pills (see below) so they're
  // never lost — with only the Smart Mix pill showing, "Train only"/"Bus only"
  // must still be reachable here.
  const renderMoreDropdown = (strategies: Strategy[]) => {
    const byKey = new Map(strategies.map((s) => [strategyKey(s), s] as const));
    const options = strategies.map((s) => ({ value: strategyKey(s), label: strategyOptionLabel(s) }));
    return (
      <Dropdown
        value=""
        placeholder="More ways…"
        ariaLabel="More transport options"
        disabled={busy}
        options={options}
        onChange={(key) => { const s = byKey.get(key); if (s) onPick(s); }}
        hideCheck
        // Portalled so the menu opens upward when there is no room below:
        // on desktop Trip detail this block is pinned to the bottom of the
        // screen (TripDetailPage, exploreBarStyle).
        portal
      />
    );
  };

  if (view === 'cards') {
    return (
      <div style={{ background: '#ffffff', borderRadius: 20, padding: '20px 18px' }}>
        <div style={{ fontWeight: 700, color: '#16241d', marginBottom: 2 }}>{heading}</div>
        {subheading && <div style={{ fontSize: 13, color: '#6c7872', marginBottom: 14 }}>{subheading}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {quick.map((s) => {
            const meta = strategyMeta(s);
            const isMultimodal = s.kind === 'multimodal';
            return (
              <button
                key={strategyKey(s)}
                type="button"
                disabled={busy}
                onClick={() => onPick(s)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
                  textAlign: 'left', padding: '14px 14px', borderRadius: 14,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  border: isMultimodal ? '1.5px solid #157a55' : '1px solid rgba(20,40,30,0.12)',
                  background: isMultimodal ? 'rgba(21,122,85,0.06)' : '#f7f9f7',
                  color: '#16241d', opacity: busy ? 0.6 : 1,
                  transition: 'border-color 120ms ease, background 120ms ease',
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>{meta.emoji}</span>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{meta.title}</span>
                <span style={{ fontSize: 12, color: '#6c7872' }}>{meta.subtitle}</span>
              </button>
            );
          })}
        </div>
        {/* Cards view shows every quick strategy as a card, so the dropdown only
            needs the "more" long tail. */}
        <div style={{ marginTop: 12, width: 200 }}>{renderMoreDropdown(more)}</div>
      </div>
    );
  }

  // compact view — planner tags (when present) or the heuristic quick pills,
  // trimmed to what fits the current width; the dropdown is always kept as the
  // escape hatch to every other strategy.
  const fit = pillsThatFit(viewportWidth);
  const planPills = planEntries ? planEntries.slice(0, Math.max(1, fit)) : null;
  const compactQuick = planPills ? [] : visibleStrategies(quick, fit);
  // Strategies not shown as pills must not disappear — fold the quick heuristics
  // into the dropdown (ahead of the "more" long tail), deduped against the pills.
  const pillKeys = new Set(
    planPills ? planPills.map((e) => strategyKey(e.strategy)) : compactQuick.map(strategyKey),
  );
  const hiddenQuick = quick.filter((s) => !pillKeys.has(strategyKey(s)));
  const compactDropdownStrategies = [...hiddenQuick, ...more.filter((s) => !pillKeys.has(strategyKey(s)))];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: expanded ? 10 : 0 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#16241d', margin: 0 }}>{heading}</h3>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
            border: 'none', background: 'transparent', color: '#157a55',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '2px 4px',
          }}
        >
          {expanded ? 'Hide' : 'Show'}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}>
            <path d="M3 4.5 L6 7.5 L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {/* nowrap keeps everything on one line; do NOT use overflow here — an
          overflow container would clip the dropdown's popup panel. */}
      {expanded && (
      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center' }}>
        {(planPills || compactQuick).map((item: AlternativePlanEntry | Strategy) => {
          const entry = 'strategy' in item ? item : null;
          const s = entry ? entry.strategy : (item as Strategy);
          const m = strategyMeta(s);
          return (
            <button
              key={entry ? entry.id : strategyKey(s)}
              type="button"
              disabled={busy}
              onClick={() => onPick(s)}
              // Planner pills explain WHY this suggestion fits the trip; the
              // 🌱 marks the one suggestion outside the user's selection.
              title={entry ? entry.reason : m.subtitle}
              style={{
                flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 999, border: '1px solid rgba(20,40,30,0.14)',
                background: '#ffffff', color: '#16241d', fontSize: 13, fontWeight: 600,
                whiteSpace: 'nowrap', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
              }}
            >
              <span aria-hidden>{entry && entry.nudge ? '🌱' : m.emoji}</span>
              {strategyShortLabel(s)}
            </button>
          );
        })}
        <div style={{ flex: '0 0 auto', width: DROPDOWN_W }}>{renderMoreDropdown(compactDropdownStrategies)}</div>
      </div>
      )}
    </div>
  );
};

export default AlternativeGenerator;
